/**
 * Module dependencies.
 */

import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Area, AreaChart, CartesianGrid, Line, LineChart, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import { usePoolInvestParticipationTimeline } from 'app/hooks/requests/pool-invest/use-pool-invest';
import { cn } from 'app/lib/utils';

/**
 * A stable colour palette for participant series.
 */

const COLORS = ['#6366f1', '#10b981', '#f59e0b', '#ef4444', '#06b6d4', '#a855f7', '#ec4899', '#84cc16'];

/**
 * Month label for the time axis (YYYY-MM).
 */

function month(date: string): string {
  return String(date).slice(0, 7);
}

/**
 * Export `PoolParticipationPanel` component.
 *
 * A global view of how the pool's split changed over time (each participant's
 * share of the weight), plus, when a participant is selected, that participant's
 * effective weight over time. Both step between rule boundaries.
 */

export function PoolParticipationPanel({ projectId }: { projectId: string }): JSX.Element | null {
  const [translate] = useTranslation();
  const { data } = usePoolInvestParticipationTimeline(projectId);
  const [selected, setSelected] = useState<string | null>(null);

  const colorById = useMemo(() => {
    const map: Record<string, string> = {};

    (data?.participants ?? []).forEach((participant, index) => {
      map[participant.id] = COLORS[index % COLORS.length];
    });

    return map;
  }, [data]);

  // Each point → a row of per-participant share percentages (of that day's total
  // weight), for the 100%-stacked area chart.
  const shareRows = useMemo(() => {
    if (!data) {
      return [];
    }

    return data.points.map(point => {
      const total = Object.values(point.weights).reduce((sum, weight) => sum + weight, 0) || 1;
      const row: Record<string, number | string> = { date: point.date };

      data.participants.forEach(participant => {
        row[participant.id] = ((point.weights[participant.id] ?? 0) / total) * 100;
      });

      return row;
    });
  }, [data]);

  const weightRows = useMemo(() => {
    if (!data || !selected) {
      return [];
    }

    return data.points.map(point => ({ date: point.date, weight: point.weights[selected] ?? 0 }));
  }, [data, selected]);

  if (!data || !data.participants.length || !data.points.length) {
    return null;
  }

  const selectedName = data.participants.find(participant => participant.id === selected)?.name ?? null;

  return (
    <section className={'rounded-xl border border-border bg-card p-5 shadow-sm'}>
      <div className={'mb-4 flex flex-wrap items-center justify-between gap-3'}>
        <h2 className={'text-sm font-semibold uppercase tracking-wide text-muted-foreground'}>
          {translate('poolInvest.participation.title')}
        </h2>

        {/* Participant chips — click to see one participant's weight over time. */}
        <div className={'flex flex-wrap gap-1.5'}>
          {data.participants.map(participant => {
            const active = selected === participant.id;

            return (
              <button
                className={cn(
                  'flex items-center gap-1.5 rounded-full border px-2 py-0.5 text-xs transition-colors',
                  active
                    ? 'border-transparent bg-muted font-medium text-foreground'
                    : 'border-border text-muted-foreground hover:bg-muted/60'
                )}
                key={participant.id}
                onClick={() => setSelected(active ? null : participant.id)}
                type={'button'}
              >
                <span className={'h-2.5 w-2.5 rounded-full'} style={{ backgroundColor: colorById[participant.id] }} />
                {participant.name || '—'}
              </button>
            );
          })}
        </div>
      </div>

      {/* Global share-over-time (100% stacked). */}
      <ResponsiveContainer height={240} width={'100%'}>
        <AreaChart data={shareRows} margin={{ top: 4, right: 8, bottom: 0, left: -16 }}>
          <CartesianGrid stroke={'hsl(var(--border))'} strokeDasharray={'3 3'} vertical={false} />
          <XAxis dataKey={'date'} fontSize={11} tickFormatter={month} tickLine={false} />
          <YAxis
            domain={[0, 100]}
            fontSize={11}
            tickFormatter={(value: number) => `${value}%`}
            tickLine={false}
            width={44}
          />
          <Tooltip
            formatter={(value: any, name: any) => [`${Number(value).toFixed(1)}%`, name]}
            labelFormatter={(label: any) => label}
          />
          {data.participants.map(participant => (
            <Area
              dataKey={participant.id}
              fill={colorById[participant.id]}
              fillOpacity={0.75}
              key={participant.id}
              name={participant.name || '—'}
              stackId={'share'}
              stroke={colorById[participant.id]}
              type={'stepAfter'}
            />
          ))}
        </AreaChart>
      </ResponsiveContainer>

      {/* Selected participant's weight over time. */}
      {selected ? (
        <div className={'mt-6'}>
          <h3 className={'mb-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground'}>
            {translate('poolInvest.participation.weightOverTime', { name: selectedName ?? '' })}
          </h3>

          <ResponsiveContainer height={180} width={'100%'}>
            <LineChart data={weightRows} margin={{ top: 4, right: 8, bottom: 0, left: -16 }}>
              <CartesianGrid stroke={'hsl(var(--border))'} strokeDasharray={'3 3'} vertical={false} />
              <XAxis dataKey={'date'} fontSize={11} tickFormatter={month} tickLine={false} />
              <YAxis allowDecimals={false} fontSize={11} tickLine={false} width={44} />
              <Tooltip labelFormatter={(label: any) => label} />
              <Line
                dataKey={'weight'}
                dot={false}
                name={selectedName || '—'}
                stroke={selected ? colorById[selected] : COLORS[0]}
                strokeWidth={2}
                type={'stepAfter'}
              />
            </LineChart>
          </ResponsiveContainer>
        </div>
      ) : null}
    </section>
  );
}
